import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, AlertCircle, Loader } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | success | failed
  const [attempts, setAttempts] = useState(0);
  const pollRef = useRef(null);
  const MAX_ATTEMPTS = 6;

  const checkStatus = async (attempt) => {
    if (!sessionId) { setStatus("failed"); return; }
    try {
      const { data } = await axios.get(`${API}/api/payments/status/${sessionId}`, { withCredentials: true });
      if (data.payment_status === "paid") {
        setStatus("success");
        clearTimeout(pollRef.current);
        setTimeout(() => navigate("/chef/dashboard"), 3500);
      } else if (attempt < MAX_ATTEMPTS) {
        pollRef.current = setTimeout(() => checkStatus(attempt + 1), 2500);
        setAttempts(attempt + 1);
      } else {
        setStatus("failed");
      }
    } catch {
      if (attempt < MAX_ATTEMPTS) {
        pollRef.current = setTimeout(() => checkStatus(attempt + 1), 2500);
      } else {
        setStatus("failed");
      }
    }
  };

  useEffect(() => {
    checkStatus(0);
    return () => clearTimeout(pollRef.current);
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center pt-16 px-6">
      <div className="max-w-md w-full text-center border border-[#D4AF37]/20 bg-[#0A0A0A] p-12">
        {status === "checking" && (
          <>
            <Loader size={48} className="text-[#D4AF37] mx-auto mb-6 animate-spin" />
            <h2 className="font-serif text-3xl text-white mb-3">Processing Payment</h2>
            <p className="text-[#A0A0A0] text-sm mb-2">Confirming your subscription...</p>
            <p className="text-[#555] text-xs">Attempt {attempts + 1} of {MAX_ATTEMPTS}</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle size={52} className="text-emerald-400 mx-auto mb-6" />
            <h2 className="font-serif text-3xl text-white mb-3" data-testid="payment-success-msg">
              Payment Successful!
            </h2>
            <p className="text-[#A0A0A0] text-sm mb-6">
              Your chef listing is now <span className="text-emerald-400">active</span>. Clients can now find you in the directory.
            </p>
            <div className="border-t border-[#D4AF37]/15 pt-6">
              <p className="text-[#555] text-xs mb-4">Redirecting to your dashboard...</p>
              <button
                onClick={() => navigate("/chef/dashboard")}
                className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB] px-8 py-3 text-sm font-medium tracking-widest uppercase transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </>
        )}
        {status === "failed" && (
          <>
            <AlertCircle size={52} className="text-amber-400 mx-auto mb-6" />
            <h2 className="font-serif text-3xl text-white mb-3" data-testid="payment-pending-msg">
              Payment Pending
            </h2>
            <p className="text-[#A0A0A0] text-sm mb-6">
              We could not confirm your payment immediately. If your payment went through, your listing will be activated shortly.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => navigate("/chef/dashboard")}
                className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB] px-8 py-3 text-sm font-medium tracking-widest uppercase transition-colors"
              >
                Go to Dashboard
              </button>
              <button
                onClick={() => { setStatus("checking"); setAttempts(0); checkStatus(0); }}
                className="border border-[#D4AF37]/40 text-[#D4AF37] px-8 py-3 text-sm tracking-wide hover:border-[#D4AF37] transition-colors"
              >
                Check Again
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
